import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, IconButton, Typography, Avatar, Button, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiMicrophone, mdiMicrophoneOff, mdiPhoneHangup, mdiClose, mdiPause, mdiAlertCircleOutline } from "@mdi/js";
import { clsx } from "@ismael1361/utils";
import { useSTT, useTTS, isSTTSupported, useIsMobile } from "shared/hooks";
import { useVoicePreferences } from "shared/contexts";
import { useThemeSettings } from "shared/contexts";
import { VUMeter } from "./VUMeter";
import styles from "./styles.module.scss";

/** Forma mínima de mensagem que o modal precisa — compatível tanto com o chat
 *  do Assistente (useAssistantMessages) quanto com o do Líder (useTeamMessages). */
export interface VoiceChatMessage {
	id: string;
	content: string;
	senderType: string;
	isStreaming?: boolean;
	error?: boolean;
	errorMessage?: string;
	metadata?: Record<string, any>;
}

export interface VoiceCallModalProps {
	open: boolean;
	onClose: () => void;
	/** Nome exibido do interlocutor IA (Assistente ou Líder). */
	leaderName: string;
	/** Letra/inicial para o avatar fallback. */
	leaderAvatarChar?: string;
	leaderAvatarUrl?: string;
	/** Envia o texto transcrito ao interlocutor (mesma função do chat texto). */
	sendMessage: (content: string) => Promise<void> | void;
	/** Lista de mensagens do chat (para detectar a resposta da IA). */
	messages: VoiceChatMessage[];
	/** True enquanto a IA está respondendo. */
	isStreaming: boolean;
	/** senderType das respostas da IA a serem lidas em voz alta. Default: "assistente". */
	replySenderType?: string;
	/**
	 * Quando fornecido, habilita aprovação/rejeição de tarefas delegadas por voz.
	 * O modal lê a confirmação em voz alta, escuta a resposta do usuário
	 * (sim/não em pt-BR) e chama esta função.
	 */
	onConfirm?: (confirmationId: string, approved: boolean, reason?: string) => Promise<void> | void;
}

// EK-NEW: parser de comandos de aprovação por voz (pt-BR).
type ConfirmDecision = "approve" | "reject" | "unknown";
function parseConfirmationCommand(text: string): ConfirmDecision {
	const t = text.toLowerCase().trim().replace(/[.,!?;:]+$/g, "");
	const APPROVE = /\b(sim|aprov[oae]r?|aprovad[oa]|confirm[oae]r?|confirmad[oa]|ok+|okay|positivo|pode\s+(ir|mandar|enviar|fazer)?|manda(\s+ver)?|vai(\s+fundo)?|beleza|blz|certo|isso(\s+mesmo)?|claro|com\s+certeza|envia(r|e)?|tudo\s+(bem|certo))\b/i;
	const REJECT = /\b(n[ãa]o|nao|rejeit[oae]r?|rejeitad[oa]|cancel[oae]r?|cancelad[oa]|abort[oae]r?|recus[oae]r?|recusad[oa]|melhor\s+n[ãa]o|esquece(r)?|deixa\s+pra\s+l[áa]|negativo|n[ãa]o\s+precisa)\b/i;
	if (APPROVE.test(t)) return "approve";
	if (REJECT.test(t)) return "reject";
	return "unknown";
}

type CallState = "init" | "listening" | "sending" | "waiting" | "speaking" | "confirming" | "muted" | "denied" | "unsupported" | "error";

const STATUS_LABEL: Record<CallState, string> = {
	init: "Conectando...",
	listening: "Ouvindo...",
	sending: "Enviando...",
	waiting: "Pensando...",
	speaking: "Falando...",
	confirming: "Aguardando sua aprovação (sim/não)...",
	muted: "Microfone mudo",
	denied: "Permissão de microfone negada",
	unsupported: "Navegador sem suporte a voz",
	error: "Ocorreu um erro",
};

export const VoiceCallModal: React.FC<VoiceCallModalProps> = ({
	open,
	onClose,
	leaderName,
	leaderAvatarChar,
	leaderAvatarUrl,
	sendMessage,
	messages,
	isStreaming,
	replySenderType = "assistente",
	onConfirm,
}) => {
	const isMobile = useIsMobile();
	const { colors } = useThemeSettings();
	const { rate, pitch, volume, voiceURI, bargeIn } = useVoicePreferences();

	const [state, setState] = useState<CallState>("init");
	const [transcript, setTranscript] = useState("");
	const [lastLeaderText, setLastLeaderText] = useState("");
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [micStream, setMicStream] = useState<MediaStream | null>(null);

	// Refs de controle de fluxo.
	const awaitingReplyRef = useRef(false);
	const lastSpokenIdRef = useRef<string | null>(null);

	// EK-NEW: confirmação por voz de tarefas delegadas.
	const [confirmingInfo, setConfirmingInfo] = useState<{ id: string; tool: string } | null>(null);
	const confirmingRef = useRef<{ id: string; tool: string } | null>(null);
	const onConfirmRef = useRef(onConfirm);
	const ttsApiRef = useRef<{ speak: (s: string) => void; cancel: () => void } | null>(null);
	useEffect(() => {
		onConfirmRef.current = onConfirm;
	}, [onConfirm]);
	const setConfirming = useCallback((val: { id: string; tool: string } | null) => {
		confirmingRef.current = val;
		setConfirmingInfo(val);
	}, []);
	const baselineMsgCountRef = useRef(0);
	const stateRef = useRef<CallState>(state);
	useEffect(() => {
		stateRef.current = state;
	}, [state]);

	// API estável do STT — evita dependência circular em handleFinalTranscript
	// (declarado antes do objeto `stt`) e mantém referência sempre atual.
	const sttApiRef = useRef<{ stop: () => void } | null>(null);

	// ── TTS ────────────────────────────────────────────────────
	const tts = useTTS({
		rate,
		pitch,
		volume,
		voiceURI,
		onStart: () => setState("speaking"),
		onEnd: () => {
			// Após o Líder falar, volta a ouvir (se não mudo/encerrado).
			if (stateRef.current === "speaking") {
				resumeListening();
			}
		},
	});

	// ── STT ────────────────────────────────────────────────────
	const handleFinalTranscript = useCallback(
		(text: string) => {
			if (!text.trim()) return;
			// Para de ouvir enquanto envia/aguarda/fala (evita captar o próprio TTS).
			sttApiRef.current?.stop();
			setTranscript(text);

			// EK-NEW: se há uma confirmação pendente, interpreta a fala como sim/não.
			const confirming = confirmingRef.current;
			if (confirming && onConfirmRef.current) {
				const decision = parseConfirmationCommand(text);
				if (decision === "approve" || decision === "reject") {
					confirmingRef.current = null;
					setConfirmingInfo(null);
					setState("sending");
					Promise.resolve(onConfirmRef.current(confirming.id, decision === "approve"))
						.then(() => {
							setState("waiting");
							awaitingReplyRef.current = true;
						})
						.catch(() => {
							setErrorMsg("Não foi possível enviar a confirmação.");
							setState("error");
						});
					return;
				}
				// Não entendi — reformula e segue ouvindo.
				const retry = "Não entendi. Diga 'sim' para aprovar ou 'não' para rejeitar.";
				setLastLeaderText(retry);
				ttsApiRef.current?.speak(retry);
				return;
			}

			// Caminho normal: envia a mensagem ao interlocutor IA.
			setState("sending");
			awaitingReplyRef.current = true;
			baselineMsgCountRef.current = messagesRef.current.length;
			Promise.resolve(sendMessage(text))
				.then(() => setState("waiting"))
				.catch(() => {
					setErrorMsg("Não foi possível enviar sua mensagem.");
					setState("error");
					awaitingReplyRef.current = false;
				});
		},
		[sendMessage],
	);

	const stt = useSTT({
		lang: "pt-BR",
		silenceMs: 1500,
		confidenceThreshold: 0.5,
		onFinalTranscript: handleFinalTranscript,
		onError: (err) => {
			if (err === "audio-capture") {
				setErrorMsg("Microfone não disponível.");
				setState("error");
			}
		},
	});

	// Mantém a API estável do STT atualizada para uso em handleFinalTranscript.
	useEffect(() => {
		sttApiRef.current = { stop: stt.stop };
	}, [stt.stop]);

	// EK-NEW: API estável do TTS — usada por handleFinalTranscript para o re-prompt.
	useEffect(() => {
		ttsApiRef.current = { speak: tts.speak, cancel: tts.cancel };
	}, [tts.speak, tts.cancel]);

	// messages ref para uso dentro de callbacks estáveis.
	const messagesRef = useRef<VoiceChatMessage[]>(messages);
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	const resumeListening = useCallback(() => {
		if (!isSTTSupported()) return;
		setTranscript("");
		stt.clear();
		stt.start();
		setState("listening");
	}, [stt]);

	// ── Detecta resposta do Líder concluída → fala ──────────────
	useEffect(() => {
		if (!awaitingReplyRef.current) return;
		if (isStreaming) return;

		// Procura a última resposta da IA ainda não falada.
		const replyMsgs = messages.filter((m) => m.senderType === replySenderType && !m.isStreaming);
		const last = replyMsgs[replyMsgs.length - 1];
		if (!last || last.id === lastSpokenIdRef.current) return;

		awaitingReplyRef.current = false;
		lastSpokenIdRef.current = last.id;

		// Caso a resposta exija confirmação de ferramenta:
		// - Com `onConfirm` (voz): o efeito dedicado abaixo cuida de ler a
		//   confirmação real e escutar a resposta do usuário.
		// - Sem `onConfirm` (fallback): orienta o usuário a aprovar no chat texto.
		const pendingConfirmation = messages.some((m) => m.senderType === "confirmation" && m.metadata?.status === "pending");

		if (last.error) {
			const txt = last.errorMessage || "O Líder encontrou um erro ao processar.";
			setLastLeaderText(txt);
			tts.speak(txt);
			return;
		}

		if (pendingConfirmation && !onConfirm) {
			const txt = "O Líder precisa da sua confirmação para executar uma ação. Toque para revisar no chat.";
			setLastLeaderText(txt);
			tts.speak(txt);
			return;
		}

		const text = (last.content || "").trim();
		if (text) {
			setLastLeaderText(text);
			tts.speak(text);
		} else {
			// Sem texto (ex.: só executou tools) — volta a ouvir.
			resumeListening();
		}
	}, [messages, isStreaming, replySenderType, tts, resumeListening, onConfirm]);

	// EK-NEW: efeito dedicado a detectar confirmações pendentes e ler em voz alta.
	// Roda quando NÃO estamos no meio de uma fala (state listening/waiting/muted) —
	// para não cortar o áudio da resposta anterior do assistente.
	useEffect(() => {
		if (!onConfirm) return;
		if (state === "speaking" || state === "sending" || state === "init") return;
		if (state === "denied" || state === "unsupported" || state === "error") return;

		const pendingConf = messages.find(
			(m) => m.senderType === "confirmation" && m.metadata?.status === "pending",
		);

		if (!pendingConf) {
			if (confirmingRef.current) {
				// A confirmação foi resolvida (aprovada/rejeitada externamente ou via voz).
				setConfirming(null);
			}
			return;
		}

		const cid = pendingConf.metadata?.confirmationId as string | undefined;
		if (!cid) return;
		if (confirmingRef.current?.id === cid) return; // já estamos confirmando esta

		const toolName = (pendingConf.metadata?.toolCall?.name as string | undefined) ?? "uma ação";
		const desc = (pendingConf.content || "").trim();
		const prompt = desc
			? `${desc}. Diga 'sim' para aprovar, ou 'não' para rejeitar.`
			: `O assistente quer executar ${toolName}. Diga 'sim' para aprovar, ou 'não' para rejeitar.`;

		setConfirming({ id: cid, tool: toolName });
		setLastLeaderText(prompt);
		// Para de ouvir antes de falar; ao terminar, onEnd do TTS chama resumeListening
		// — aí o STT volta a captar a resposta do usuário (sim/não).
		stt.stop();
		tts.speak(prompt);
	}, [messages, state, onConfirm, stt, tts, setConfirming]);

	// ── Abertura / fechamento ───────────────────────────────────
	const startCall = useCallback(async () => {
		setErrorMsg(null);
		setTranscript("");
		setLastLeaderText("");
		awaitingReplyRef.current = false;
		lastSpokenIdRef.current = null;

		if (!isSTTSupported()) {
			setState("unsupported");
			return;
		}

		// Solicita permissão de microfone (CA-01) e captura stream para o VU meter.
		// echoCancellation + noiseSuppression evitam que o áudio do TTS reentre pelo mic
		// e dispare o barge-in (EK-452) falsamente.
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
			});
			setMicStream(stream);
		} catch {
			setState("denied");
			return;
		}

		resumeListening();
	}, [resumeListening]);

	const endCall = useCallback(() => {
		awaitingReplyRef.current = false;
		setConfirming(null);
		stt.abort();
		tts.cancel();
		micStream?.getTracks().forEach((t) => t.stop());
		setMicStream(null);
		setState("init");
		onClose();
	}, [stt, tts, micStream, onClose, setConfirming]);

	useEffect(() => {
		if (open) {
			startCall();
		}
		// Cleanup ao fechar externamente.
		return () => {
			if (!open) return;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Garante liberação do microfone ao desmontar.
	useEffect(() => {
		return () => {
			micStream?.getTracks().forEach((t) => t.stop());
			tts.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ── Controles ───────────────────────────────────────────────
	const toggleMute = useCallback(() => {
		if (state === "muted") {
			resumeListening();
		} else {
			stt.stop();
			tts.cancel();
			setState("muted");
		}
	}, [state, stt, tts, resumeListening]);

	const interruptSpeaking = useCallback(() => {
		tts.cancel();
		resumeListening();
	}, [tts, resumeListening]);

	// EK-452 · barge-in: cancela o TTS quando o usuário começa a falar.
	// Opt-in via preferência `bargeIn` — desligado por padrão porque o cancelamento
	// de eco do navegador raramente é perfeito; quando ativado, usamos um threshold
	// conservador + janela de "graça" maior, e exigimos voz sustentada para reduzir
	// falsos positivos com o próprio áudio do TTS retornando pelo microfone.
	useEffect(() => {
		if (!bargeIn) return;
		if (state !== "speaking" || !micStream) return;

		const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
		if (!AudioCtx) return;

		let ctx: AudioContext | null = null;
		let analyser: AnalyserNode | null = null;
		let source: MediaStreamAudioSourceNode | null = null;
		let rafId: number | null = null;
		let cancelled = false;

		try {
			ctx = new AudioCtx();
			analyser = ctx.createAnalyser();
			analyser.fftSize = 1024;
			analyser.smoothingTimeConstant = 0.2;
			source = ctx.createMediaStreamSource(micStream);
			source.connect(analyser);
		} catch {
			return;
		}

		const timeData = new Float32Array(analyser.fftSize);
		const RMS_THRESHOLD = 0.22; // Muito acima do ruído captado do próprio TTS
		const REQUIRED_FRAMES = 14; // ~230ms — exige fala sustentada, não estalo
		const GRACE_MS = 900; // Ignora ~1s para o engine "engatar" sem disparar
		const startedAt = performance.now();
		let activeFrames = 0;

		const tick = () => {
			if (cancelled || !analyser) return;
			analyser.getFloatTimeDomainData(timeData);
			let sum = 0;
			for (let i = 0; i < timeData.length; i++) {
				const v = timeData[i];
				sum += v * v;
			}
			const rms = Math.sqrt(sum / timeData.length);

			const elapsed = performance.now() - startedAt;
			if (elapsed >= GRACE_MS && rms > RMS_THRESHOLD) {
				activeFrames++;
				if (activeFrames >= REQUIRED_FRAMES) {
					cancelled = true;
					interruptSpeaking();
					return;
				}
			} else {
				activeFrames = 0;
			}

			rafId = requestAnimationFrame(tick);
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			cancelled = true;
			if (rafId !== null) cancelAnimationFrame(rafId);
			try {
				source?.disconnect();
			} catch {
				/* noop */
			}
			if (ctx && ctx.state !== "closed") {
				ctx.close().catch(() => undefined);
			}
		};
	}, [bargeIn, state, micStream, interruptSpeaking]);

	const discardInterim = useCallback(() => {
		stt.clear();
		setTranscript("");
	}, [stt]);

	// Exibe a transcrição parcial em tempo real durante a escuta.
	useEffect(() => {
		if (state === "listening" && stt.interimTranscript) {
			setTranscript(stt.interimTranscript);
		}
	}, [stt.interimTranscript, state]);

	// Sincroniza estado de permissão negada vindo do STT.
	useEffect(() => {
		if (stt.permissionDenied) setState("denied");
	}, [stt.permissionDenied]);

	const avatarColor = colors.primary;
	const isSpeaking = state === "speaking";
	const vuMode: "listening" | "speaking" | "idle" = state === "listening" ? "listening" : isSpeaking ? "speaking" : "idle";

	const showError = state === "denied" || state === "unsupported" || state === "error";

	const errorContent = useMemo(() => {
		if (state === "unsupported") {
			return {
				title: "Voz indisponível neste navegador",
				body: "Seu navegador não suporta a Web Speech API. Use uma versão recente do Chrome, Edge ou Safari para conversar por voz.",
			};
		}
		if (state === "denied") {
			return {
				title: "Permissão de microfone negada",
				body: "Para conversar por voz, habilite o microfone nas configurações do navegador (ícone de cadeado na barra de endereço) e tente novamente.",
			};
		}
		return { title: "Ops, algo deu errado", body: errorMsg || "Tente encerrar e reabrir o modo de voz." };
	}, [state, errorMsg]);

	return (
		<Dialog
			open={open}
			onClose={endCall}
			fullScreen={isMobile}
			maxWidth="xs"
			fullWidth
			PaperProps={{ className: styles.callPaper }}
		>
			<div className={styles.callRoot}>
				<div className={styles.callHeader}>
					<Typography className={styles.callTitle}>e-Keep — Chamada com {leaderName}</Typography>
					<IconButton
						onClick={endCall}
						size="small"
						className={styles.headerClose}
						aria-label="Fechar"
					>
						<Icon path={mdiClose} size={0.9} />
					</IconButton>
				</div>

				{showError ? (
					<div className={styles.errorArea}>
						<Icon path={mdiAlertCircleOutline} size={2} className={styles.errorIcon} />
						<Typography className={styles.errorTitle}>{errorContent.title}</Typography>
						<Typography className={styles.errorBody}>{errorContent.body}</Typography>
						<Button variant="contained" onClick={endCall} className={styles.errorButton}>
							Voltar ao chat
						</Button>
					</div>
				) : (
					<>
						<div className={styles.avatarArea}>
							<div className={clsx(styles.avatarPulse, isSpeaking && styles.avatarPulseActive)} style={{ borderColor: avatarColor }}>
								<Avatar src={leaderAvatarUrl} className={styles.avatar} sx={{ bgcolor: avatarColor, width: 96, height: 96, fontSize: 40 }}>
									{leaderAvatarChar || leaderName.charAt(0).toUpperCase()}
								</Avatar>
							</div>
							<Typography className={styles.leaderName}>{leaderName}</Typography>
							{/* EK-NEW: rótulo do estado adaptado quando há confirmação pendente. */}
							<Typography className={styles.statusLabel}>
								{confirmingInfo
									? state === "speaking"
										? "Lendo a aprovação..."
										: state === "listening"
											? "Responda: sim ou não?"
											: STATUS_LABEL[state]
									: STATUS_LABEL[state]}
							</Typography>
							{confirmingInfo && (
								<Tooltip title={`Tarefa: ${confirmingInfo.tool}`}>
									<span style={{
										marginTop: 6,
										display: "inline-flex",
										alignItems: "center",
										gap: 6,
										padding: "3px 10px",
										borderRadius: 999,
										background: "rgba(251, 191, 36, 0.18)",
										color: "#facc15",
										fontSize: 12,
										fontWeight: 600,
									}}>
										⚠ Aprovação pendente
									</span>
								</Tooltip>
							)}
						</div>

						<div className={styles.vuArea}>
							<VUMeter mode={vuMode} stream={micStream} color={avatarColor} />
						</div>

						<div className={styles.transcriptArea}>
							{isSpeaking && lastLeaderText ? (
								<Typography className={styles.transcriptText}>{lastLeaderText}</Typography>
							) : transcript ? (
								<Typography className={styles.transcriptText}>{transcript}</Typography>
							) : (
								<Typography className={styles.transcriptHint}>
									{state === "listening" ? "Pode falar — envio automático após uma pausa." : " "}
								</Typography>
							)}
							{state === "listening" && transcript && (
								<Button size="small" onClick={discardInterim} className={styles.discardButton}>
									Descartar
								</Button>
							)}
						</div>

						<div className={styles.controls}>
							<Tooltip title={state === "muted" ? "Ativar microfone" : "Silenciar microfone"}>
								<IconButton
									onClick={toggleMute}
									className={clsx(styles.controlButton, state === "muted" && styles.controlButtonActive)}
									aria-label={state === "muted" ? "Ativar microfone" : "Silenciar microfone"}
								>
									<Icon path={state === "muted" ? mdiMicrophoneOff : mdiMicrophone} size={1.1} />
								</IconButton>
							</Tooltip>

							{isSpeaking && (
								<Tooltip title="Pausar leitura">
									<IconButton onClick={interruptSpeaking} className={styles.controlButton} aria-label="Pausar leitura">
										<Icon path={mdiPause} size={1.1} />
									</IconButton>
								</Tooltip>
							)}

							<Tooltip title="Encerrar chamada">
								<IconButton onClick={endCall} className={clsx(styles.controlButton, styles.hangupButton)} aria-label="Encerrar chamada">
									<Icon path={mdiPhoneHangup} size={1.1} />
								</IconButton>
							</Tooltip>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
};
